<template>
  <div class="shop-summary">
    <slot name="top" />
    <div class="shop-summary__actions">
      <CheckboxComponent v-model="showDefault" :label="$t('showDefaultSelection')" />
      <ButtonComponent :icon="['fal', 'print']" @click="print" type="button" />
    </div>
    <Alert
      v-if="!value.categories.filter(HideDefaultFilter).length"
      class="shop-summary__no-upgrades"
    >
      {{ $t('noUpgradesSelected') }}
    </Alert>

    <table>
      <template
        v-for="(category, ci) in value.categories
          .filter(c => ConditionFilter(c, selected))
          .filter(HideDefaultFilter)"
      >
        <tr :key="ci" class="shop-summary__category">
          <td>{{ category.label }}</td>
          <td>
            <slot name="category-right" :item="category" />
          </td>
        </tr>
        <tr
          v-for="(subcategory, si) in category.categories
            .filter(c => ConditionFilter(c, selected) && c.items.length)
            .filter(HideDefaultFilter)"
          :key="[ci, si].join('_')"
          class="shop-summary__subcategory"
        >
          <td>{{ subcategory.label }}:</td>
          <td>
            <Product show-image :value="getSelected(subcategory)" />
          </td>
        </tr>
      </template>
      <tr class="shop-summary__total">
        <td>
          {{ $t('total') }}
          <span style="opacity: 0.5; font-size: 0.6em">({{ $t('includingVat') }})</span>
        </td>
        <td>{{ total }}</td>
      </tr>
    </table>
    <div class="shop-summary__other-inquiries">{{ $t('shopContactSellerForOtherInquiries') }}</div>
    <slot name="bottom" />
  </div>
</template>

<script>
import { Currency, ConditionFilter } from './utils'
import { CheckboxComponent } from 'vue-elder-checkbox'
import { AlertComponent as Alert } from 'vue-elder-alert'
import { ButtonComponent } from 'vue-elder-button'

import Product from './Product/Card'

export default {
  props: {
    value: Object,
    toggleState: false,
  },
  data() {
    return {
      showDefault: false,
    }
  },
  computed: {
    selected() {
      if (!this.value) return []
      let items = []
      let target = this.value.categories

      const extract = x => {
        items.push(this.getSelected(x))
        if (x.categories && x.categories.length) x.categories.forEach(extract)
      }

      target.forEach(extract)

      return items.filter(Boolean)
    },
    total() {
      return Currency(
        this.selected.reduce((res, cur) => (res += cur.isDefault ? 0 : cur.product.gross), 0),
      )
    },
  },
  methods: {
    ConditionFilter,
    HideDefaultFilter(category) {
      if (this.showDefault) return true

      let filter = i => {
        let selected = i.items.find(item => item.selected)
        if (!selected) return false
        return !selected.isDefault
      }

      if ('items' in category) return filter(category)

      return category.categories.map(filter).some(Boolean)
    },
    getSelected(item) {
      return (
        item.selected ||
        (item.items && item.items.find(i => i.selected)) || {
          product: {
            name: this.$t('noShopDefaultProduct'),
          },
          isDummy: true,
          selected: true,
          isDefault: true,
        }
      )
    },
    print() {
      let bodyHTML = document.querySelector('.shop') || document.querySelector('.shop-summary')
      if (!bodyHTML) return
      let print = window.open('')
      print.document.write(
        `<html><head>${document.head.innerHTML}</head><body>${bodyHTML.outerHTML}</body></html>`,
      )
      print.document.close()

      setTimeout(() => {
        print.print()
        print.close()
      }, 500)
    },
  },
  created() {
    this.showDefault = this.toggleState
  },
  components: {
    Product,
    CheckboxComponent,
    Alert,
    ButtonComponent,
  },
}
</script>

<style lang="scss">
@import './styles/_variables';
.shop-summary {
  max-width: 950px;
  width: 100%;
  margin: 0 auto;

  table {
    width: 100%;
    border-collapse: collapse;

    td {
      padding: 1rem 0;
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 1.5rem;
    align-content: center;
    align-items: center;
    padding-bottom: 2rem;
  }

  &__category {
    font-weight: bold;
    font-size: 1.5em;
    border-bottom: 1px solid $border-color;
  }

  &__subcategory {
    td:first-child {
      font-weight: bold;
      vertical-align: top;
      font-size: 1em;
    }
  }

  &__total {
    font-weight: bold;
    font-size: 1.5em;

    border-top: 2px solid $border-color;

    td:last-child {
      text-align: right;
    }

    @media (max-width: $shop-responsive) {
      font-size: 1.3em;
    }
  }

  &__show-default {
    display: flex;
    justify-content: flex-end;
    padding-bottom: 2rem;
  }

  &__no-upgrades {
    margin: 2rem auto;
    max-width: 350px;
    font-size: 0.9em;
    .alert__content {
      justify-content: center;
    }
  }
  &__other-inquiries {
    text-align: center;
    opacity: 0.5;
    margin-top: 2rem;
  }

  .shop-product-card {
    width: 100%;
    margin-left: auto;

    @media (max-width: $shop-responsive) {
      padding: 0.5rem;
    }
  }
}

@media print {
  .shop-navigation,
  .shop-summary__actions,
  .shop-summary__other-inquiries,
  .shop-sign__metadata,
  .elder-stepper__breadcrumbs,
  .shop-signer-summary__form {
    display: none !important;
  }

  .shop {
    border: none !important;
  }

  .shop-product-card__properties {
    gap: initial !important; // Chrome has issues with print if gap is set
  }

  .shop-signer-summary {
    border: none;

    .card__header {
      display: none;
    }
  }
}
</style>
